<template>
  <v-layout row justify-center class="toolbar-container">
    <multi-selection-tool-button
      id="refresh-tool"
      icon="refresh"
      :icon-size="22"
      :icon-color="'#767676'"
      :disabled="false"
      :tooltip="$t('Refresh')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="startAction(
        objects.edited,
        'refresh',
        emitEvent,
        'multiple-selection-refresh'
      )"
    />

    <div class="line" />

    <multi-selection-tool-button
      id="select-all-tool"
      icon="library_add_check"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="objects.selected === objects.total"
      :tooltip="$t('SelectAll')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="emitEvent('multiple-selection-select-all')"
    />
    <multi-selection-tool-button
      id="elements-in-use-tool"
      icon="library_books"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="false"
      :selected="elementsInUse.selected"
      :tooltip="$t('SelectElementsInUse')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="startAction(
        objects.edited,
        'toggleElementsInUse',
        toggleElementsInUse,
        !elementsInUse.selected
      )"
    />
    <multi-selection-tool-button
      id="invert-selection-tool"
      icon="flip"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="false"
      :tooltip="$t('InvertSelection')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="emitEvent('multiple-selection-invert-selection')"
    />
    <multi-selection-tool-button
      id="clear-selection-tool"
      icon="filter_none"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="objects.selected === 0"
      :tooltip="$t('ClearSelection')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="emitEvent('multiple-selection-clear-selection')"
    />

    <div class="line" />

    <multi-selection-tool-button
      id="delete-tool"
      icon="delete"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="
        !canUseTool('destroy') ||
        editTool.selected ||
        objects.selected === 0
      "
      :tooltip="$t('DeleteSelected')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="dialogs.deleteObjects = true"
    />

    <div class="line" />

    <multi-selection-tool-button
      id="edit-tool"
      icon="edit"
      :icon-size="20"
      :icon-color="'#767676'"
      :selected="editTool.selected"
      :disabled="!canUseTool('update')"
      :tooltip="$t('Edit')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="startAction(
        objects.edited,
        'leaveEditionMode',
        changeEditionMode,
        !editTool.selected
      )"
    />
    <multi-selection-tool-button
      id="edit-selected-tool"
      icon="mdi-pencil-plus"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="!editTool.selected || objects.selected === 0"
      :tooltip="$t('EditSelected')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="toggleEditSelected"
    />

    <div class="line" />

    <multi-selection-tool-button
      id="details-tool"
      icon="article"
      :icon-size="20"
      :icon-color="'#767676'"
      :disabled="false"
      :selected="details.selected"
      :tooltip="$t('Details')"
      :tooltip-position="'left'"
      :tooltip-color="'var(--app-primary-base)'"
      :button-style="buttonStyle"
      @click="startAction(
        objects.edited,
        'toggleDetails',
        toggleDetails,
        !details.selected
      )"
    />

    <app-dialog-box
      :open="dialogs.leaveEditionMode"
      :title="$t('LeaveEditionMode')"
      :content="leaveEditionModeDialogMessage"
      :main-action-label="$t('No')"
      @mainAction="dialogs.leaveEditionMode = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="
        executeAction('leaveEditionMode', changeEditionMode, false)
      "
    />

    <app-dialog-box
      :open="dialogs.refresh"
      :title="$t('RefreshData')"
      :content="refreshDialogMessage"
      :main-action-label="$t('No')"
      @mainAction="dialogs.refresh = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="
        executeAction('refresh', emitEvent, 'multiple-selection-refresh')
      "
    />

    <app-dialog-box
      :open="dialogs.deleteObjects"
      :title="$t('DeleteObjects')"
      :content="deleteDialogMessage"
      :main-action-label="$t('No')"
      :secondary-action-label="$t('Yes')"
      @mainAction="dialogs.deleteObjects = false"
      @secondaryAction="executeAction('deleteObjects', emitEvent, 'multiple-selection-delete')"
    />

    <app-dialog-box
      :open="closureDelete.dialogOpen"
      :title="$t('DeleteObjects')"
      :content="deleteClosuresDialogMessage"
      :main-action-label="numClosuresAssociatedIsLessSelected ? $t('No') : $t('Cancel')"
      :secondary-action-label="$t('Yes')"
      :secondary-action-disable="numClosuresAssociatedIsLessSelected"
      @mainAction="$emit('closeDialogDeleteClosure')"
      @secondaryAction="emitEvent('multiple-selection-delete-closure')"
    />
  </v-layout>
</template>

<script>
import MultiSelectionToolButton from "@/components/Home/Forms/_Atoms/TableToolbarButton";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { bus } from "@/main.js";

export default {
  name: "MultiselectToolbar",

  components: {
    MultiSelectionToolButton,
    AppDialogBox,
  },

  mixins: [checkPermissionsMixin],

  props: {
    layer: {
      type: String,
      required: true
    },

    closureDelete: {
      type: Object,
    },
  },

  data() {
    return {
      buttonStyle: {
        margin: '8px',
      },

      objects: {
        total: undefined,
        selected: 0,
        edited: false
      },

      dialogs: {
        refresh: false,
        leaveEditionMode: false,
        deleteObjects: false
      },

      editTool: {
        selected: false
      },

      elementsInUse: {
        selected: false,
      },

      details: {
        selected: false,
      },

      editSelectedTool: {
        selected: false,
      },
    }
  },

  mounted() {
    /**
     * Opens a channel to receive updates on the total number of objects
     */
    bus.$on("multiple-selection-update-number-objects", numberObjects => {
      this.objects.total = numberObjects;
    });

    /**
     * Opens a channel to receive updates on the number of objects selected
     */
    bus.$on("multiple-selection-update-number-selected-objects", numberSelectedObjects => {
      this.objects.selected = numberSelectedObjects;
    });

    /**
     * Opens a channel to receive updates on the edition state of the objects
     */
    bus.$on("multiple-selection-deactivate-edit-selected", () => {
      this.editSelectedTool.selected = false;
    });

    /**
     * Opens a channel to receive updates on the edition state of the objects
     */
    bus.$on("multiple-selection-update-edition-state", objectsAreEdited => {
      this.objects.edited = objectsAreEdited;
    });

    /**
     * Opens a channel to receive updates on the state of the edit tool
     */
    bus.$on("multiple-selection-save", () => {
      this.editTool.selected = false;
    });
  },

  destroyed() {
    // bus.$off("change-multi-selection-toolbar-state");
    bus.$off("multiple-selection-update-number-objects");
    bus.$off("multiple-selection-update-number-selected-objects");
    bus.$off("multiple-selection-update-edition-state");
    bus.$off("multiple-selection-deactivate-edit-selected");
    bus.$off("multiple-selection-save");
    // bus.$off("multiple-selection-set-data-to-export");
  },

  computed: {
    permissionName() {
      return this.getPermissionNameByLayer(this.layer);
    },

    numClosuresAssociated() {
      if (this.closureDelete.associatedCables) {
        return Object.keys(this.closureDelete.associatedCables).length;
      } else {
        return null;
      }
    },

    numClosuresAssociatedIsLessSelected() {
      if (this.closureDelete.selected) {
        return this.numClosuresAssociated < this.closureDelete.selected.length;
      } else {
        return null;
      }
    },

    leaveEditionModeDialogMessage() {
      return (
        this.$i18n.t("UnsavedChanges") +
        "\n" +
        this.$i18n.t("CheckLeaveEditionMode") +
        "\n" +
        this.$i18n.t("ChangesWillBeLost")
      );
    },

    refreshDialogMessage() {
      return (
        this.$i18n.t("UnsavedChanges") +
        "\n" +
        this.$i18n.t("CheckRefreshData") +
        "\n" +
        this.$i18n.t("ChangesWillBeLost")
      );
    },

    /**
     * Composes the Delete dialog message according to the given object type.
     * @returns {string}
     */
    deleteDialogMessage() {
      let mainMessage = this.$i18n.t("CheckDeleteObjects");
      let secondaryMessage =
        "\n \n " + this.$i18n.t("RemoveAssociatedObjectWarning");
      if (
        this.isTechnicalSite ||
        this.isTechnicalPoint ||
        this.layer.includes("closure")
      ) {
        if (this.isTechnicalSite) {
          secondaryMessage += this.$i18n.tc("TechnicalLocal", 1);
        } else if (this.isTechnicalPoint) {
          secondaryMessage +=
            this.$i18n.tc("Closure", 1) +
            "\n " +
            this.$i18n.t("SelectedClosureHasCablesAssociated") +
            "\n \n " +
            this.$i18n.t("JoinPathwayMultiSelect");
        } else if (this.layer.includes("closure")) {
          secondaryMessage =
            "\n \n " + this.$i18n.t("SelectedClosureHasCablesAssociated");
        }
        return mainMessage + secondaryMessage + ".";
      } else return mainMessage;
    },

    deleteClosuresDialogMessage() {
      if (this.closureDelete.associatedCables) {
        if (this.numClosuresAssociatedIsLessSelected) {
          return (
            this.numClosuresAssociated +
            " " +
            this.$i18n.t("DeleteClosureWithoutAssociatedCables")
          );
        } else {
          return this.$i18n.t("OnlyClosureCablesAssociated");
        }
      } else {
        return "";
      }
    },

    isTechnicalSite() {
      return (
        this.layer.includes("optical_line_termination") ||
        this.layer.includes("fiber_distribution_point")
      );
    },

    isTechnicalPoint() {
      return (
        this.layer.includes("pole") ||
        this.layer.includes("manhole") ||
        this.layer.includes("cabinet") ||
        this.layer.includes("toby")
      );
    },
  },

  watch: {
    layer() {
      this.changeEditionMode(false);
    }
  },

  methods: {
    /**
     * Emits event (for the Multi Selection table) to initiate a certain action.
     * @param {String }eventName -  name of the event to be emitted.
     */
    emitEvent(eventName) {
      bus.$emit(eventName);
    },

    /**
     * Checks if the action can be immediately executed or not.
     * If not it opens a dialog box for the user to verify that he wants to go through with the action.
     * Else it executes the action immediately.
     * @param {Boolean} dialogCondition - condition to check if a verification is needed or not.
     * @param {String} dialog - name of the variable that controls the appearance of the dialog box.
     * @param action - action to be executed.
     * @param actionParameter - parameter to feed the action
     */
    startAction(dialogCondition, dialog, action, actionParameter) {
      if (dialogCondition) {
        this.dialogs[dialog] = true;
      } else {
        action(actionParameter);
      }
    },

    /**
     * Toggles the edition mode.
     */
    changeEditionMode(newValue) {
      this.editTool.selected = newValue;
      bus.$emit("multiple-selection-update-mode", this.editTool.selected);
    },

    /**
     * Toggles the elements in use.
     */
    toggleElementsInUse(newValue) {
      this.elementsInUse.selected = newValue;
      bus.$emit("multiple-selection-toggle-elements-in-use", this.elementsInUse.selected);
    },

    /**
     * Toggles the details.
     */
    toggleDetails(newValue) {
      this.details.selected = newValue;
      bus.$emit("multiple-selection-toggle-details", this.details.selected);
    },

    /**
     * Toggles the details.
     */
    toggleEditSelected() {
      if (!this.editSelectedTool.selected) {
        this.editSelectedTool.selected = !this.editSelectedTool.selected;
        bus.$emit("multiple-selection-activate-edit-selected");
      } else {
        bus.$emit("startEditSelectedDeactivation", "fromToolbar");
      }
    },

    canUseTool(action) {
      return this.userHasFibercloudPermission(this.permissionName, action);
    },

    /**
     * Executes the given action and closes the respective dialog box.
     * @param {String} dialog - name of the variable that controls the appearance of the dialog box.
     * @param action - action to be executed.
     * @param actionParameter
     */
    executeAction(dialog, action, actionParameter = undefined) {
      if (actionParameter !== undefined) {
        action(actionParameter);
      } else {
        action();
      }
      this.dialogs[dialog] = false;
    },
  }
}
</script>

<style scoped>
.toolbar-container {
  background-color: #FFF;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  flex-wrap: nowrap;
  flex-direction: column;
  width: 48px;
  align-items: center;
}

.line {
  height: 1px;
  width: 15px;
  background: #767676;
}

.v-tooltip {
  border-radius: 8px;
}

.tool-icon.selected path {
  fill: var(--app-primary-base);
  stroke: var(--app-primary-base);
}

</style>


// WEBPACK FOOTER //
// src/components/Multiselect/MultiselectToolbar.vue